<script setup lang="ts">
import BrandSquare from "./BrandSquare.vue";
import { assistantHref } from "../assistant-pages";

/**
 * DOIT AI Hub — the post-login landing page.
 *
 * The Hub's one job is to get you to an answer in as few moves as possible,
 * so it opens with the thing you came to do: a line you can type into. Under
 * it sit a few of the operator's own sample questions (one click straight
 * into that assistant, pre-filled), then the grid of assistants the caller's
 * AD groups allow — each tile carrying the one line you need to pick it.
 *
 * Deliberately absent: a hero headline and welcome paragraph (nobody reads
 * a landing page they visit daily), a filter box (the ask bar is the better
 * answer, and most people see a handful of assistants, not dozens), and the
 * assistant count + help pill. Explanations of how access works live in the
 * empty state, where they are actually needed.
 *
 * The grid holds ONLY real assistants. "Request a new AI" is a ghost tile at
 * the end of the grid; "My Knowledge Bases" lives in the top nav (App.vue) —
 * neither is an assistant, so neither is a real card.
 *
 * Below the grid, "Other assistants": every released assistant the caller
 * cannot open, locked, from the catalog (`/api/assistants/catalog`). Those
 * come from a separate call on purpose — `assistants` above feeds the ask bar
 * and the sample questions, and must hold only what the caller can use.
 */
import { computed, onMounted, ref, watch } from "vue";
import { listVisibleAssistants, type Assistant } from "../api/conversations";
import {
  assistantTint,
  iconOf,
  isSelfContained,
  samplesOf,
} from "../assistant-visuals";
import { resolveAssistantIcon } from "../assistant-icons";
import AssistantCard from "./AssistantCard.vue";
import HubAskBar from "./HubAskBar.vue";
import RequestAiDialog from "./RequestAiDialog.vue";
import { getCatalog, type CatalogAssistant } from "../api/accessRequests";
import AccessRequestDialog from "./AccessRequestDialog.vue";
import LockedAssistantCard from "./LockedAssistantCard.vue";

const props = defineProps<{
  devUser?: string;
  /** `display_name` from /api/profile — greets by first name when known. */
  displayName?: string | null;
  /** `preference.default_assistant_slug` — the ask bar's default target. */
  preferredSlug?: string | null;
}>();

const assistants = ref<Assistant[]>([]);
const loaded = ref(false);
const requestDialogOpen = ref(false);
const catalog = ref<CatalogAssistant[]>([]);
const asking = ref<CatalogAssistant | null>(null);

// Reload when the dev-user picker switches identity: the list is per caller.
async function load(): Promise<void> {
  try {
    const [visible, cat] = await Promise.all([
      listVisibleAssistants(props.devUser),
      getCatalog(props.devUser),
    ]);
    assistants.value = visible;
    catalog.value = cat;
  } finally {
    loaded.value = true;
  }
}

/** Locked tiles: in the catalog, not usable. Anything `/visible` returned
 * is usable whatever the catalog says, so it is never shown twice. */
const locked = computed(() => {
  const usable = new Set(assistants.value.map((a) => a.slug));
  return catalog.value.filter((c) => !c.accessible && !usable.has(c.slug));
});
const pilotSlugs = computed(
  () =>
    new Set(
      catalog.value.filter((c) => c.maturity === "pilot").map((c) => c.slug),
    ),
);

function onRequested(slug: string, createdAt: string | null, id: number): void {
  catalog.value = catalog.value.map((c) =>
    c.slug === slug
      ? {
          ...c,
          request_status: "open",
          request: {
            id,
            status: "open",
            created_at: createdAt,
            handled_at: null,
          },
        }
      : c,
  );
}
onMounted(load);
watch(() => props.devUser, load);

/** Time-of-day greeting. Uses the visitor's local clock; purely cosmetic. */
const greeting = computed(() => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 18) return "Good afternoon";
  return "Good evening";
});

/** First name only — "Good afternoon, Elfar" reads like a person talking;
 * the full "Elfar Thorarinsson" reads like a database row. */
const firstName = computed(() => {
  const n = (props.displayName ?? "").trim();
  if (!n) return "";
  return n.split(/\s+/)[0];
});

/**
 * Up to three real sample questions, taken one per assistant so the strip
 * shows the breadth of what's available rather than three variations of the
 * same assistant. Operators who set no `settings.samples` get no strip —
 * better nothing than invented questions.
 *
 * These pills submit on click, so only samples that are answerable exactly
 * as written qualify — a template like "Forbered mig til mødet med <kunde>"
 * would reach the model with the placeholder intact. An assistant whose
 * samples are all templates simply contributes nothing here; it still shows
 * them inside its own chat, where they are editable before sending.
 *
 * `isSelfContained` catches syntactic placeholders only. A sample can need
 * input without saying so ("Here are my meeting notes — write minutes"), and
 * no pattern will catch that. The durable fix is an explicit operator flag
 * on the sample rather than a cleverer regex.
 */
const suggestions = computed(() => {
  const out: Array<{
    slug: string;
    name: string;
    icon?: string;
    text: string;
  }> = [];
  for (const a of primary.value) {
    const s = samplesOf(a).find(isSelfContained);
    if (s) out.push({ slug: a.slug, name: a.name, icon: iconOf(a), text: s });
    if (out.length >= 3) break;
  }
  return out;
});

/**
 * ADR 0063 — access and relevance are separate. Assistants made for your
 * teams, made for nobody in particular, or used recently come first; the
 * rest of what you MAY open (IGEL for a salesperson) sits under "More
 * assistants", one click away rather than crowding the grid. When nothing
 * is set aside the Hub looks exactly as it did.
 */
const forYou = computed(() =>
  assistants.value.filter((a) => a.for_you !== false),
);
const more = computed(() =>
  assistants.value.filter((a) => a.for_you === false),
);
/** The grid's main list: for-you first, or everything if none qualify. */
const primary = computed(() =>
  forYou.value.length ? forYou.value : more.value,
);
const secondary = computed(() => (forYou.value.length ? more.value : []));

const MORE_KEY = "choir.hub.moreOpen";
function readMoreOpen(): boolean {
  try {
    return window.localStorage.getItem(MORE_KEY) === "1";
  } catch {
    return false;
  }
}
const moreOpen = ref(readMoreOpen());
function toggleMore(): void {
  moreOpen.value = !moreOpen.value;
  try {
    window.localStorage.setItem(MORE_KEY, moreOpen.value ? "1" : "0");
  } catch {
    // A convenience only; without storage it just starts closed.
  }
}

function suggestionHref(slug: string, text: string): string {
  return `#/chat/${slug}?q=${encodeURIComponent(text)}`;
}
</script>

<template>
  <div class="hub">
    <!-- One line, not a hero. The ask bar below is the actual welcome. -->
    <h1 class="hub__greeting display">
      {{ greeting }}<template v-if="firstName">, {{ firstName }}</template>
    </h1>

    <div class="hub__ask">
      <HubAskBar :assistants="assistants" :preferred-slug="preferredSlug" />
    </div>

    <!-- Full Hub width, not the ask bar's — three real questions then fit on
         one row instead of wrapping into a ragged second line. -->
    <div v-if="suggestions.length" class="hub__suggestions">
      <a
        v-for="s in suggestions"
        :key="s.slug + s.text"
        class="hub__suggestion"
        :href="suggestionHref(s.slug, s.text)"
        :title="`${s.name} — ${s.text}`"
        :style="{ '--tint': assistantTint(s.slug) }"
      >
        <component
          :is="resolveAssistantIcon(s.icon)"
          v-if="resolveAssistantIcon(s.icon)"
          :size="14"
          :stroke-width="2"
        />
        <span class="hub__suggestion-text">{{ s.text }}</span>
      </a>
    </div>

    <div
      v-if="!loaded"
      class="hub__grid"
      aria-busy="true"
      aria-label="Loading assistants"
    >
      <div v-for="i in 8" :key="i" class="card-skeleton glass">
        <span class="skeleton skeleton--badge"></span>
        <span class="skeleton skeleton--name"></span>
        <span class="skeleton skeleton--line"></span>
      </div>
    </div>

    <template v-else-if="assistants.length > 0">
      <h2 class="hub__label">
        {{ secondary.length ? "For you" : "Your assistants" }}
      </h2>
      <div class="hub__grid">
        <AssistantCard
          v-for="(a, i) in primary"
          :key="a.slug"
          :slug="a.slug"
          :name="a.name"
          :description="a.description"
          :icon="iconOf(a)"
          :href="assistantHref(a.slug)"
          :index="i"
          :pilot="pilotSlugs.has(a.slug)"
        />
        <!-- Not an assistant, so it reads as a ghost tile rather than a
             card: same footprint, none of the weight. -->
        <button
          type="button"
          class="hub__ghost rise-in"
          :style="{
            animationDelay: `${Math.min(primary.length, 12) * 45}ms`,
          }"
          @click="requestDialogOpen = true"
        >
          <span class="hub__ghost-mark" aria-hidden="true">+</span>
          <span class="hub__ghost-label">Request a new AI</span>
        </button>
      </div>
      <template v-if="secondary.length">
        <button
          type="button"
          class="hub__more"
          :aria-expanded="moreOpen"
          @click="toggleMore"
        >
          {{ moreOpen ? "▾" : "▸" }} More assistants you can use ·
          {{ secondary.length }}
        </button>
        <div v-if="moreOpen" class="hub__grid">
          <AssistantCard
            v-for="(a, i) in secondary"
            :key="a.slug"
            :slug="a.slug"
            :name="a.name"
            :description="a.description"
            :icon="iconOf(a)"
            :href="assistantHref(a.slug)"
            :index="i"
            :pilot="pilotSlugs.has(a.slug)"
          />
        </div>
      </template>
    </template>

    <div v-else class="hub__empty glass">
      <BrandSquare class="hub__empty-mark" size="3rem" />
      <p class="hub__empty-title">No assistants assigned yet</p>
      <p class="hub__empty-body">
        You'll see an assistant here once your AD group has been granted one.
        Talk to your team lead — or pitch a use case to the AI Board.
      </p>
      <button
        type="button"
        class="hub__empty-btn"
        @click="requestDialogOpen = true"
      >
        Request a new AI
      </button>
    </div>

    <template v-if="loaded && locked.length">
      <h2 class="hub__label hub__label--locked">Other assistants</h2>
      <p class="hub__locked-sub">
        You can't open these yet. Click one to ask for access.
      </p>
      <div class="hub__grid">
        <LockedAssistantCard
          v-for="(a, i) in locked"
          :key="a.slug"
          :assistant="a"
          :index="i"
          @open="asking = $event"
        />
      </div>
    </template>

    <RequestAiDialog v-model="requestDialogOpen" :dev-user="devUser" />
    <AccessRequestDialog
      :assistant="asking"
      :dev-user="devUser"
      @close="asking = null"
      @requested="onRequested"
    />
  </div>
</template>

<style scoped>
.hub {
  /* A *definite* width — not `max-width` — so the Hub fills the shell and
     centres. As an auto-margin grid item, `max-width` alone would let it
     collapse to its content width (the old "narrow main content" bug);
     `width: min(...)` resolves against the shell's full-width column. */
  width: min(1320px, 100%);
  margin-inline: auto;
  padding: var(--space-5) var(--space-4) var(--space-8);
}

.hub__more {
  margin: var(--space-5) 0 var(--space-3);
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-muted);
  cursor: pointer;
}
.hub__more:hover {
  color: var(--color-ink);
}
/* ── Greeting ───────────────────────────────────────────────────────── */
.hub__greeting {
  margin: var(--space-2) 0 var(--space-4);
  font-size: clamp(1.5rem, 2.6vw, 2rem);
  font-weight: 460;
  color: var(--color-ink);
}

/* ── Ask block ──────────────────────────────────────────────────────── */
.hub__ask {
  width: min(54rem, 100%);
}
.hub__suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin: var(--space-3) 0 var(--space-6);
  padding-left: 0.15rem;
}
.hub__suggestion {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  padding: 0.36rem 0.75rem;
  border-radius: var(--radius-pill);
  border: 1px solid var(--glass-border);
  background: color-mix(in srgb, var(--color-surface) 55%, transparent);
  color: var(--color-muted);
  font-size: 0.82rem;
  line-height: 1.35;
  text-decoration: none;
  transition:
    color var(--dur-1) var(--ease-out),
    border-color var(--dur-1) var(--ease-out),
    background var(--dur-1) var(--ease-out);
}
.hub__suggestion svg {
  flex: none;
  color: var(--tint, var(--color-accent));
}
.hub__suggestion-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 22rem;
}
.hub__suggestion:hover {
  color: var(--color-ink);
  background: var(--color-surface);
  border-color: color-mix(
    in srgb,
    var(--tint, var(--color-accent)) 35%,
    transparent
  );
}

/* ── Grid ───────────────────────────────────────────────────────────── */
/* The label normally inherits its top gap from `.hub__suggestions`, which
   sits between it and the ask bar. When no assistant carries sample
   questions that block doesn't render at all, and the label collides with
   the ask bar (seen on PCAI, which runs a single sample-less assistant).
   Restore the gap only in that adjacency so the suggestions case keeps its
   existing rhythm instead of double-spacing. */
.hub__ask + .hub__label {
  margin-top: var(--space-6);
}
.hub__label {
  margin: 0 0 var(--space-3);
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-ink);
}
/* `auto-fill` + `1fr` keeps both extremes honest: with many assistants the
   cards stretch evenly to fill each row, while a lone card stays one column
   wide (auto-fill keeps the empty tracks) instead of ballooning into a
   full-width slab. */
.hub__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(17.5rem, 1fr));
  gap: var(--space-4);
}
/* Pointing at one tile pushes the rest back. Contrast against its
   neighbours is what actually makes a tile "pop" — a tile can only stand
   out relative to something, and lifting it alone leaves eight equally
   bright rectangles. `:has()` keeps this precise: hovering the gaps between
   tiles does nothing, and browsers without `:has()` simply skip the rule
   and get the old behaviour. */
@media (hover: hover) {
  .hub__grid:has(> *:hover) > *:not(:hover) {
    opacity: 0.55;
  }
}

@media (max-width: 560px) {
  .hub__grid {
    grid-template-columns: 1fr;
  }
  .hub__ghost {
    min-height: 0;
  }
}

/* ── Locked assistants ─────────────────────────────────────────────── */
.hub__label--locked {
  margin-top: var(--space-6);
  margin-bottom: var(--space-1);
}
.hub__locked-sub {
  margin: 0 0 var(--space-3);
  font-size: 0.86rem;
  color: var(--color-muted);
}

/* ── "Request a new AI" ghost tile ──────────────────────────────────── */
.hub__ghost {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.45rem;
  min-height: 9.5rem;
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  border: 1px dashed color-mix(in srgb, var(--color-muted) 40%, transparent);
  background: transparent;
  color: var(--color-muted);
  text-align: left;
  cursor: pointer;
  transition:
    border-color var(--dur-2) var(--ease-out),
    color var(--dur-2) var(--ease-out),
    opacity var(--dur-2) var(--ease-out),
    background var(--dur-2) var(--ease-out);
}
.hub__ghost-mark {
  display: grid;
  place-items: center;
  width: 2.6rem;
  height: 2.6rem;
  margin-bottom: 0.2rem;
  border-radius: 30%;
  border: 1px dashed color-mix(in srgb, var(--color-muted) 40%, transparent);
  font-size: 1.1rem;
  line-height: 1;
  transition: border-color var(--dur-2) var(--ease-out);
}
.hub__ghost-label {
  font-size: 1.02rem;
  font-weight: 650;
}
.hub__ghost:hover {
  color: var(--color-accent);
  border-color: color-mix(in srgb, var(--color-accent) 45%, transparent);
  background: color-mix(in srgb, var(--color-accent) 4%, transparent);
}
.hub__ghost:hover .hub__ghost-mark {
  border-color: color-mix(in srgb, var(--color-accent) 45%, transparent);
}
.hub__ghost:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}

/* ── Skeleton ───────────────────────────────────────────────────────── */
.card-skeleton {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-lg);
  min-height: 9.5rem;
}
.skeleton {
  display: block;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    color-mix(in srgb, var(--color-muted) 14%, transparent) 0%,
    color-mix(in srgb, var(--color-muted) 6%, transparent) 50%,
    color-mix(in srgb, var(--color-muted) 14%, transparent) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite linear;
}
.skeleton--badge {
  flex: none;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 30%;
}
.skeleton--name {
  height: 0.95rem;
  width: 55%;
}
.skeleton--line {
  height: 0.75rem;
  width: 80%;
}
@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
}

/* ── Empty state ────────────────────────────────────────────────────── */
.hub__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-5);
  border-radius: var(--radius-lg);
}
/* The brand square stands in for the aurora blob here. An empty state is
   where a product has the least to show and the most to say about whose it
   is -- a generic gradient said nothing. */
.hub__empty-mark {
  margin-bottom: var(--space-2);
}
.hub__empty-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 650;
  color: var(--color-ink);
}
.hub__empty-body {
  margin: 0;
  max-width: 40ch;
  color: var(--color-muted);
  line-height: 1.6;
}
.hub__empty-btn {
  margin-top: var(--space-3);
  padding: 0.55rem 1.1rem;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
  color: var(--color-inverse, #fff);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--dur-1) var(--ease-out);
}
.hub__empty-btn:hover {
  background: var(--color-accent-hover);
}
</style>
